import { useMutation, useQueryClient } from '@tanstack/react-query'
import Team from '../../model/Team'
import { authService, dataSource } from '../../service/domain/_service.config'
import { setActiveChallengeId } from '../challenge/activeChallenge'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useChallengeId } from '../challenge/useChallengeId'
import { ENTITLEMENT_QUERY_KEY } from '../membership/useChallengeAccess'
import { TEAM_MEMBERSHIPS_QUERY_KEY } from '../membership/useTeamMemberships'
import { TEAMS_QUERY_KEY } from './useTeams'

export function useCreateTeam(challengeId?: string) {
  const queryClient = useQueryClient()
  const activeChallengeId = useChallengeId()
  const resolvedChallengeId = challengeId || activeChallengeId
  const { data: challengeContext } = useChallengeContext(resolvedChallengeId)

  return useMutation({
    mutationFn: async ({ name, hasPaid }: { name: string; hasPaid?: boolean }) => {
      const authID = await authService.getCurrentUserAuthID()
      if (!authID) throw new Error('No authenticated user found')

      const user = await dataSource.getUserByAuthID(authID)
      if (!user) throw new Error('No user found')

      if (!resolvedChallengeId) throw new Error('No challenge')
      const challengeID = resolvedChallengeId

      const existing = await dataSource.getTeamMembershipForChallenge(user.id, challengeID)
      if (existing) throw new Error('Already on a team in this challenge')

      const team = Team.create(name, user, {
        challengeID,
        targetMemberCount: challengeContext?.teamSizeTarget,
        hasPaid,
      })
      await dataSource.saveTeam(team)
      await dataSource.addTeamMember(user.id, team.id, challengeID)

      return { team, userID: user.id, challengeID }
    },
    onSuccess: ({ challengeID, userID }) => {
      setActiveChallengeId(challengeID)
      queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY(challengeID) })
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERSHIPS_QUERY_KEY(userID) })
      queryClient.invalidateQueries({
        queryKey: ENTITLEMENT_QUERY_KEY(userID, challengeID),
      })
    },
  })
}
